/**
 * The canonical subscriber command registry. Renderers may choose their own
 * presentation, but must execute these `run` functions rather than reimplement
 * an action in a button or composer.
 */
export type CommandConfirmation = { kind: 'cost' | 'destructive'; message: string };

export interface CommandContext {
  projectId: string | null | undefined;
  sync: () => Promise<void>;
  guardian: () => Promise<void>;
  refresh: () => Promise<void>;
}

export interface Command {
  name: 'sync' | 'guardian' | 'refresh';
  label: string;
  description: string;
  requiresProject: boolean;
  confirmation?: CommandConfirmation;
  /** TODO(ocn-S661-P0): use this field to gate commands by subscriber product. */
  entitlement?: string;
  run: (ctx: CommandContext) => Promise<void>;
}

export const COMMANDS: readonly Command[] = [
  {
    name: 'sync', label: 'Sync Project', description: 'Sync this project from GitHub', requiresProject: true,
    confirmation: { kind: 'cost', message: 'Sync Project starts a pipeline and may use compute. Continue?' },
    run: async ({ sync }) => sync(),
  },
  {
    name: 'guardian', label: 'Run Guardian', description: 'Run a Guardian review for this project', requiresProject: true,
    confirmation: { kind: 'cost', message: 'Run Guardian uses compute. Continue?' },
    run: async ({ guardian }) => guardian(),
  },
  {
    name: 'refresh', label: 'Refresh', description: 'Refresh this project’s latest status', requiresProject: true,
    run: async ({ refresh }) => refresh(),
  },
] as const;

export function commandForName(name: string): Command | undefined {
  return COMMANDS.find((command) => command.name === name);
}

/** Parses only a complete command; `/sync notes` is intentionally not a command yet. */
export function commandFromText(text: string): Command | undefined {
  const match = text.trim().match(/^\/([a-z-]+)$/i);
  return match ? commandForName(match[1].toLowerCase()) : undefined;
}

export function commandsMatchingSlash(input: string): readonly Command[] {
  if (!input.startsWith('/')) return [];
  const query = input.slice(1).trim().toLowerCase();
  return COMMANDS.filter((command) => !query || command.name.startsWith(query) || command.label.toLowerCase().startsWith(query));
}

/** One guarded execution seam used by button, menu, and typed command renderers. */
export async function executeCommand(command: Command, ctx: CommandContext, confirm?: (message: string) => boolean): Promise<void> {
  if (command.requiresProject && !ctx.projectId) {
    throw new Error('Select a project before running this command.');
  }
  if (command.confirmation && !(confirm ?? ((message) => window.confirm(message)))(command.confirmation.message)) return;
  await command.run(ctx);
}
